import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { puzzlesApi } from '../api/client';
import { getLocalProgress } from '../lib/localProgress';
import { Puzzle, Difficulty } from '../types';

const difficultyInfo: Record<Difficulty, { name: string; color: string; emoji: string }> = {
  beginner: { name: '初学者', color: 'from-pink-400 to-rose-400', emoji: '🌸' },
  easy: { name: '简单', color: 'from-green-500 to-emerald-600', emoji: '🌱' },
  medium: { name: '中等', color: 'from-yellow-500 to-orange-600', emoji: '⚡' },
  hard: { name: '困难', color: 'from-red-500 to-rose-600', emoji: '🔥' },
};

export default function LevelSelectPage() {
  const { difficulty } = useParams<{ difficulty?: string }>();
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty>(
    (difficulty as Difficulty) || 'beginner'
  );
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (difficulty && ['beginner', 'easy', 'medium', 'hard'].includes(difficulty)) {
      setSelectedDifficulty(difficulty as Difficulty);
    }
  }, [difficulty]);

  useEffect(() => {
    const fetchPuzzles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await puzzlesApi.getList(selectedDifficulty);
        setPuzzles(response.puzzles);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '加载关卡失败';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPuzzles();
  }, [selectedDifficulty]);

  const info = difficultyInfo[selectedDifficulty];

  return (
    <div className="py-4 sm:py-8">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">选择关卡</h1>
        <p className="text-sm sm:text-base text-gray-500">选择难度和关卡开始游戏</p>
      </div>

      {/* Difficulty Tabs */}
      <div className="flex justify-center gap-2 sm:gap-4 mb-6 sm:mb-8 px-2">
        {(Object.entries(difficultyInfo) as [Difficulty, typeof info][]).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setSelectedDifficulty(key)}
            className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all text-sm sm:text-base touch-manipulation ${
              selectedDifficulty === key
                ? `bg-gradient-to-r ${value.color} text-white shadow-lg`
                : 'bg-white/80 hover:bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <span className="mr-1 sm:mr-2">{value.emoji}</span>
            {value.name}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin w-10 h-10 sm:w-12 sm:h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">加载中...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center py-12">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            重试
          </button>
        </div>
      )}

      {/* Levels Grid */}
      {!isLoading && !error && (() => {
        // Get local progress once outside the map loop for better performance
        const localProgress = getLocalProgress();
        
        return (
        <motion.div
          key={selectedDifficulty}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 sm:gap-3"
        >
          {puzzles.map((puzzle, index) => {
            // Handle both snake_case (from API) and camelCase (from types)
            const levelNum = (puzzle as any).level_number ?? puzzle.levelNumber ?? (index + 1);
            const locallyCompleted = localProgress.completedPuzzles.has(puzzle.id);
            const locallyUnlocked = levelNum === 1 || 
              localProgress.completedByDifficulty[selectedDifficulty]?.includes(levelNum - 1);
            
            // Combine server and local progress
            const isCompleted = puzzle.progress?.completed || locallyCompleted;
            const isUnlocked = puzzle.unlocked !== false || locallyUnlocked;
            const hasProgress = puzzle.progress && !puzzle.progress.completed && !locallyCompleted;

            return (
              <motion.div
                key={puzzle.id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.02 }}
              >
                {isUnlocked ? (
                  <Link
                    to={`/game/${puzzle.id}`}
                    className={`aspect-square flex items-center justify-center rounded-lg sm:rounded-xl text-sm sm:text-lg font-semibold transition-all touch-manipulation active:scale-95 ${
                      isCompleted
                        ? 'bg-green-100 text-green-600 border-2 border-green-400'
                        : hasProgress
                        ? 'bg-yellow-100 text-yellow-600 border-2 border-yellow-400'
                        : `bg-gradient-to-br ${info.color} text-white hover:scale-105 active:scale-95 shadow-sm hover:shadow-md`
                    }`}
                  >
                    {isCompleted ? '✓' : levelNum}
                  </Link>
                ) : (
                  <div className="aspect-square flex items-center justify-center rounded-lg sm:rounded-xl text-sm sm:text-lg font-semibold bg-gray-100 text-gray-400 cursor-not-allowed">
                    🔒
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
        );
      })()}

      {/* Legend */}
      <div className="mt-6 sm:mt-8 flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500 px-2">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-gradient-to-br ${info.color}`} />
          <span>可游玩</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-yellow-100 border-2 border-yellow-400" />
          <span>进行中</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-green-100 border-2 border-green-400" />
          <span>已完成</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-gray-100 text-gray-400 flex items-center justify-center text-[10px] sm:text-xs">
            🔒
          </div>
          <span>未解锁</span>
        </div>
      </div>
    </div>
  );
}

